import { LinkSharedProps, cssToken } from "@certn/cascadia";
import { decorations } from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";
import { useMemo } from "react";
import { mergeProps } from "react-aria";
import { Link as AriaLink, LinkProps } from "react-aria-components";

import { Icon } from "../../foundations/icons/Icon/Icon";
import { getTextStyle } from "../../foundations/typography/styles";

interface Props extends LinkProps, LinkSharedProps {
  isNewTab?: boolean;
}

export function Link(props: Props) {
  const {
    linkStyle = "default",
    textStyle,
    textColor = linkStyle === "default" ? "text-info" : undefined,
    leftIcon,
    rightIcon,
    hasUnderline = true,
    testID,
    isNewTab,
    ...linkProps
  } = props;

  const className = useMemo(() => {
    return css(
      textStyle && getTextStyle(textStyle),
      { color: cssToken(textColor) || "inherit" },
      (props.href || props.onPress) && { cursor: "pointer" }
    );
  }, [textColor, textStyle, props.href, props.onPress]);

  return (
    <AriaLink
      target={isNewTab ? "_blank" : undefined}
      {...mergeProps(linkProps, {
        className: cx(
          hasUnderline && styles.underline,
          (leftIcon || rightIcon) && styles.linkWithIcon,
          styles.link,
          className
        ),
      })}
      data-testid={testID}
    >
      {(renderProps) => {
        const children =
          typeof props.children === "function"
            ? props.children(renderProps)
            : props.children;

        if (!leftIcon && !rightIcon) {
          return children;
        }

        const iconColor = renderProps.isDisabled ? "icon-disabled" : textColor;

        return (
          <>
            {leftIcon && <Icon name={leftIcon} type="mini" color={iconColor} />}
            <span className={cx(hasUnderline && styles.underline)}>
              {children}
            </span>
            {rightIcon && (
              <Icon name={rightIcon} type="mini" color={iconColor} />
            )}
          </>
        );
      }}
    </AriaLink>
  );
}

const styles = {
  link: css({
    cursor: "pointer",
    outline: "none",
    borderRadius: "var(--border-radius-2)",
    color: "inherit",
    "&[data-disabled]": {
      cursor: "not-allowed",
      color: "var(--text-disabled)",
    },
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
  }),
  linkWithIcon: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-1)",
    textDecoration: "none",
    width: "fit-content",
  }),
  underline: css({
    textDecoration: decorations["font-deco-underline"],
  }),
};
